<template>
  <div class="horizontal-navigation">
    <!-- 左箭头 -->
    <div v-if="canScrollLeft" class="arrow-button left-arrow" @click="scrollLeft" :class="{ disabled: !canScrollLeft }">
      <ArrowIcon />
    </div>

    <!-- 导航区域 -->
    <div class="navigation-container" ref="containerRef">
      <div class="navigation-area" ref="contentRef">
        <span v-for="(item, index) in items" :key="index" class="nav-button" @click="handleClick(index)">
          {{ item.label }}
        </span>
      </div>
    </div>

    <!-- 右箭头 -->
    <div v-if="canScrollRight" class="arrow-button right-arrow" @click="scrollRight" :class="{ disabled: !canScrollRight }">
      <ArrowIcon />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from "vue";
import ArrowIcon from "./ArrowIcon.vue";

interface NavItem {
  label: string;
  value: string | number;
  type: number;
}

interface Props {
  items?: NavItem[];
  scrollStep?: number; // 每次滚动的像素数
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  scrollStep: 100,
});

const emit = defineEmits<{
  "item-click": [item: NavItem, index: number];
}>();

// Refs
const containerRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

// 计算属性
const scrollPosition = ref(0);

// 检查滚动状态
const updateScrollButtons = () => {
  if (!containerRef.value || !contentRef.value) return;

  const containerWidth = containerRef.value.clientWidth;
  const contentWidth = contentRef.value.scrollWidth;

  canScrollLeft.value = scrollPosition.value > 0;
  canScrollRight.value = scrollPosition.value < contentWidth - containerWidth;
};

// 向左滚动
const scrollLeft = () => {
  if (!containerRef.value) return;

  const newPosition = Math.max(0, scrollPosition.value - props.scrollStep);
  scrollPosition.value = newPosition;
  containerRef.value.scrollLeft = newPosition;
  updateScrollButtons();
};

// 向右滚动
const scrollRight = () => {
  if (!containerRef.value || !contentRef.value) return;

  const containerWidth = containerRef.value.clientWidth;
  const contentWidth = contentRef.value.scrollWidth;
  const maxScroll = contentWidth - containerWidth;

  const newPosition = Math.min(
    maxScroll,
    scrollPosition.value + props.scrollStep,
  );
  scrollPosition.value = newPosition;
  containerRef.value.scrollLeft = newPosition;
  updateScrollButtons();
};

// 处理导航项点击
const handleClick = (index: number) => {
  emit("item-click", props.items[index], index);
};

// 监听容器大小变化
const resizeObserver = new ResizeObserver(updateScrollButtons);

onMounted(() => {
  if (containerRef.value) {
    resizeObserver.observe(containerRef.value);
  }
  if (contentRef.value) {
    resizeObserver.observe(contentRef.value);
  }
  updateScrollButtons();
});

onUnmounted(() => {
  resizeObserver.disconnect();
});

// 监听items变化
watch(
  () => props.items,
  () => {
    setTimeout(updateScrollButtons, 100);
  },
);
</script>

<style lang="less" scoped>
.horizontal-navigation {
  position: relative;
  display: flex;
  align-items: center;
  margin: 8px 16px;
}

.navigation-container {
  flex: 1;
  overflow-x: auto;
  scroll-behavior: smooth;
  min-width: 0;

  /* 隐藏滚动条 */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */

  &::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari and Opera */
  }
}

.navigation-area {
  display: flex;
  white-space: nowrap;
  margin: 0;
  gap: 8px;
}

.nav-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  background: white;
  color: #616961;
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 100px;
  border: 1px solid #f2f2f2;
  user-select: none;
  white-space: nowrap;
  flex-shrink: 0;

  &:active {
    background: #e8e8e8;
    transform: scale(0.9);
  }
}

.arrow-button {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 32px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  z-index: 1;
  background: transparent;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.left-arrow {
  left: 0;
  justify-content: flex-start;
  background: linear-gradient(to right, #f9faf9 40%, rgba(249, 250, 249, 0));
}

.right-arrow {
  right: 0;
  justify-content: flex-end;
  background: linear-gradient(to left, #f9faf9 40%, rgba(249, 250, 249, 0));

  svg {
    transform: rotate(180deg);
  }
}
</style>
